import { KeyboardEvent, ReactNode, useId } from 'react';
import { Box, Flex } from 'reflexbox';
import styled, { ThemeColors, css } from 'styled-components';

import { palettes, tokens } from '../theme/tokens';
import { Paragraph } from './Text';

const trackColor = (props: {
	$on?: boolean;
	error?: boolean;
	tintColor?: keyof ThemeColors;
	theme: { colors: ThemeColors };
	hover?: boolean;
}) => {
	if (props.$on) {
		if (props.error) return tokens.error.icon;
		if (props.tintColor === 'green') return tokens.success.icon;
		if (props.tintColor) return props.theme.colors[props.tintColor];
		return props.hover ? palettes.heatherGray['800'] : tokens.text.primary;
	}
	return props.hover ? tokens.border.strong : tokens.border.default;
};

const ContainerEnabled = css<{ $on?: boolean; error?: boolean; tintColor?: keyof ThemeColors }>`
	background: ${(props) => trackColor(props)};

	&:hover,
	&[data-preview='hover'] {
		background: ${(props) => trackColor({ ...props, hover: true })};
	}

	&:focus-visible {
		outline: none;
		background: ${(props) => trackColor({ ...props, hover: true })};
	}
`;

const ContainerDisabled = css`
	background: ${palettes.heatherGray['100']};
	cursor: not-allowed;
`;

const Container = styled.div<{
	$on?: boolean;
	error?: boolean;
	large?: boolean;
	tintColor?: keyof ThemeColors;
	$disabled?: boolean;
}>`
	padding: ${(props) => (props.large ? 4 : 3)}px;
	width: ${(props) => (props.large ? 48 : 32)}px;
	height: ${(props) => (props.large ? 28 : 20)}px;
	border-radius: ${tokens.radius.full}px;
	box-sizing: border-box;
	cursor: pointer;

	${(props) => (props.$disabled ? ContainerDisabled : ContainerEnabled)}
`;

const Indicator = styled.div<{ $on?: boolean; large?: boolean }>`
	width: ${(props) => (props.large ? 20 : 14)}px;
	height: ${(props) => (props.large ? 20 : 14)}px;
	border-radius: 50%;
	background: ${tokens.surface.white};
	transition: 0.2s ease-out transform;

	${(props) =>
		props.$on &&
		css`
			transform: translateX(${props.large ? 20 : 12}px);
		`}
`;

export function Switch(props: {
	label?: string | ReactNode;
	on: boolean;
	onChange: (on: boolean) => void;
	error?: boolean;
	large?: boolean;
	tintColor?: keyof ThemeColors;
	disabled?: boolean;
	'data-preview'?: string;
}) {
	const labelId = useId();

	const onChange = () => {
		if (!props.disabled) {
			props.onChange(!props.on);
		}
	};

	const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
		if (event.key === 'Enter' || event.key === ' ') {
			event.preventDefault();
			onChange();
		}
	};

	const control = (
		<Box flexShrink={0} flexGrow={0}>
			<Container
				$on={props.on}
				error={props.error}
				large={props.large}
				tintColor={props.tintColor}
				$disabled={props.disabled}
				tabIndex={props.disabled ? -1 : 0}
				role="switch"
				aria-checked={props.on}
				aria-disabled={props.disabled}
				aria-labelledby={props.label ? labelId : undefined}
				data-preview={props['data-preview']}
				onClick={onChange}
				onKeyDown={onKeyDown}
			>
				<Indicator $on={props.on} large={props.large} />
			</Container>
		</Box>
	);

	if (props.label) {
		return (
			<Flex alignItems="center">
				<Box mr={3}>{control}</Box>
				{typeof props.label === 'string' ? (
					<Paragraph id={labelId} flexGrow={1} flexShrink={1} onClick={onChange}>
						{props.label}
					</Paragraph>
				) : (
					<span id={labelId}>{props.label}</span>
				)}
			</Flex>
		);
	}

	return control;
}
